import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../form.css';

const ConsequenceListComponent = () => {
  const [consequences, setConsequences] = useState([]);
  const [tagColors, setTagColors] = useState({});
  const [availableTags, setAvailableTags] = useState([]);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);

  const navigate = useNavigate();

  const getRandomColor = () => {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  };

  // Fetch les conséquences via l'API une seule fois
  useEffect(() => {
    const fetchConsequences = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/get_all_consequences`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });
        const data = await response.json();
        setConsequences(data);

        // Générer les couleurs des tags et récupérer les langues
        const newTagColors = {};
        const allTags = [];
        const allLanguages = new Set();
        data.forEach((consequence) => {
          consequence.tags.forEach((tag) => {
            if (!newTagColors[tag]) {
              newTagColors[tag] = getRandomColor();
            }
            if (!allTags.includes(tag)) {
              allTags.push(tag);
            }
          });
          allLanguages.add(consequence.language);
        });
        setTagColors(newTagColors);
        setAvailableTags(allTags);
        setAvailableLanguages([...allLanguages]);
      } catch (error) {
        console.error('Erreur lors du chargement des conséquences :', error);
      }
    };

    fetchConsequences();
  }, []); // Pas de dépendances pour éviter les appels en boucle

  useEffect(() => {
    if (consequences.length > 0) {
      const savedY = sessionStorage.getItem("consequenceListScrollY");
      if (savedY) {
        window.scrollTo({ top: parseInt(savedY, 10), behavior: "instant" });
        sessionStorage.removeItem("consequenceListScrollY");
      }
    }
  }, [consequences]);

  // Fonction pour rediriger vers l'éditeur de conséquence
  const handleEdit = (consequenceId) => {
    sessionStorage.setItem("consequenceListScrollY", window.scrollY);
    navigate(`/consequence_editor/${consequenceId}`);
  };

  // Fonction pour rediriger vers le créateur de conséquence
  const handleCreateNew = () => {
    navigate("/consequence_creator");
  };

  // Gérer les changements des filtres
  const handleSearchChange = (e) => setSearchText(e.target.value);
  const handleLanguageChange = (e) => setSelectedLanguage(e.target.value);
  const handleTagToggle = (tag) => {
    setSelectedTags((prevSelectedTags) =>
      prevSelectedTags.includes(tag)
        ? prevSelectedTags.filter((t) => t !== tag)
        : [...prevSelectedTags, tag]
    );
  };

  // Filtrage des conséquences
  const filteredConsequences = consequences.filter((consequence) => {
    const matchesSearchText =
      consequence.title.toLowerCase().includes(searchText.toLowerCase()) ||
      consequence.description.toLowerCase().includes(searchText.toLowerCase());
    const matchesLanguage =
      selectedLanguage === '' || consequence.language === selectedLanguage;
      const matchesTags =
      selectedTags.length === 0 || 
      consequence.tags.some((tag) => selectedTags.includes(tag)); //

    return matchesSearchText && matchesLanguage && matchesTags;
  });

  return (
    <div className="consequence-list-container">
      <div className="consequence-list-header">
        <button onClick={handleCreateNew} className="game-config-list-blue-button">
          Créer une nouvelle conséquence
        </button>
      </div>
      {/* Filtres */}
      <div className="consequence-filters">
        {/* Recherche Texte */}
        <input
          type="text"
          placeholder="Rechercher une conséquence..."
          value={searchText}
          onChange={handleSearchChange}
          className="consequence-filter-search"
        />

        {/* Filtre par Langue */}
        <select
          value={selectedLanguage}
          onChange={handleLanguageChange}
          className="consequence-filter-language"
        >
          <option value="">Toutes les langues</option>
          {availableLanguages.map((language) => (
            <option key={language} value={language}>
              {language}
            </option>
          ))}
        </select>

        {/* Filtre par Tags */}
        <div className="consequence-filter-tags">
          {availableTags.map((tag) => (
            <label key={tag} className="consequence-tag-filter">
              <input
                type="checkbox"
                checked={selectedTags.includes(tag)}
                onChange={() => handleTagToggle(tag)}
              />
              <span
                className="consequence-tag-label"
                style={{ backgroundColor: tagColors[tag] }}
              >
                {tag}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Liste des Conséquences */}
      <ul className="consequence-list">
        {filteredConsequences.map((consequence) => (
          <li key={consequence._id} className="consequence-list-item">
            <div className="consequence-list-item-top">
              <div className="consequence-list-item-label">
                <div className={`consequence-language-label label-${consequence.language}`}>
                  {consequence.language}
                </div>
                <span className="consequence-list-name">{consequence.title}</span>
              </div>
              <button
                onClick={() => handleEdit(consequence._id)}
                className="consequence-list-orange-button"
              >
                Éditer
              </button>
            </div>

            <div className="consequence-tags">
              {consequence.tags.map((tag, index) => (
                <span
                  key={index}
                  className={`consequence-tag`}
                  style={{ backgroundColor: tagColors[tag] }}
                >
                  {tag}
                </span>
              ))}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ConsequenceListComponent;
